import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface LineChartSeries {
  name: string;
  data: number[];
  color?: string;
}

interface LineChartProps {
  labels: string[];
  series: LineChartSeries[];
  title?: string;
  height?: number;
  loading?: boolean;
  /** Format Y-axis ticks and tooltip values (e.g. currency / tokens). Defaults to a localized number. */
  valueFormatter?: (value: number) => string;
  /** Optional compact formatter for Y-axis TICKS only (keeps the tooltip on valueFormatter).
   *  Use it to drop verbose units from axis labels so they don't wrap/clip. */
  yTickFormatter?: (value: number) => string;

  xTickAngle?: number;
  /** Unit shown as a vertical Y-axis label (e.g. "Credits", "Tokens", "Requests"). */
  yUnit?: string;
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];


function niceTicks(max: number, count = 5): number[] {
  if (!isFinite(max) || max <= 0) return [0, 1];
  const rawStep = max / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = mag * (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 4 ? 2.5 : norm < 7 ? 5 : 10);
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step * 1e-6; v += step) ticks.push(Number(v.toPrecision(12)));
  return ticks;
}

export default function LineChart({
  labels,
  series,
  title,
  height = 300,
  loading = false,
  valueFormatter = (v) => (typeof v === 'number' ? v.toLocaleString() : `${v}`),
  yTickFormatter,
  xTickAngle,
  yUnit,
}: LineChartProps) {
  if (loading) {
    return (
      <div className="animate-pulse">
        {title && <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>}
        <div style={{ height }} className="bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!labels || labels.length === 0 || !series || series.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-gray-400 text-sm">No data available</p>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = labels.map((label, index) => {
    const dataPoint: any = { name: label };
    series.forEach((s) => {
      dataPoint[s.name] = s.data[index] || 0;
    });
    return dataPoint;
  });

  // Enhanced axis (round Y ticks + show every X label angled) is OPT-IN via `yUnit`, so it
  // applies only to the AI trend charts and does NOT change the 6 other dashboards that share
  // this component (they pass neither yUnit nor xTickAngle → original Recharts behavior).
  const enhanced = yUnit != null;

  // With many points (e.g. a 30-day daily range) Recharts otherwise hides most date labels
  // ("Jun 20 missing"). For enhanced charts, show EVERY label — angled so they don't overlap.
  const many = chartData.length > 12;
  const effAngle = xTickAngle ?? (enhanced && many ? -40 : 0);
  const showAll = xTickAngle != null || (enhanced && many);

  // Round Y-axis ticks (enhanced charts only); others keep Recharts' auto domain/ticks.
  const maxVal = chartData.reduce((m, d) => {
    for (const s of series) { const v = Number(d[s.name]) || 0; if (v > m) m = v; }
    return m;
  }, 0);
  const yTicks = enhanced ? niceTicks(maxVal) : null;

  return (
    <div>
      {title && <h4 className="text-lg font-semibold text-gray-900 mb-4">{title}</h4>}

      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: yUnit ? 28 : 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: effAngle ? (many ? 10 : 11) : 12, fill: '#6b7280' }}
            stroke="#9ca3af"
            interval={showAll ? 0 : 'preserveStartEnd'}
            angle={effAngle}
            textAnchor={effAngle ? 'end' : 'middle'}
            height={effAngle ? 64 : 30}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
            tickFormatter={(v) => (yTickFormatter ?? valueFormatter)(Number(v))}
            ticks={yTicks ?? undefined}
            domain={yTicks ? [0, yTicks[yTicks.length - 1]] : undefined}
            width={yUnit ? 68 : 56}
            label={yUnit ? { value: yUnit, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12, fill: '#6b7280' } } : undefined}
          />
          <Tooltip
            formatter={(value: any) => valueFormatter(Number(value))}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
          />
          {series.map((s, index) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={s.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
